/** `sections/mobile-menu.liquid` — `<mobile-menu>`: fullscreen slide-in menu with
 * two levels (root + Shop/Learn sub-panels) and a native state selector. Opened
 * by the header's `[data-mobile-menu-toggle]` (toggles `body.mobile-menu-open`). */

const BODY_OPEN_CLASS = 'mobile-menu-open'
const BODY_SUB_CLASS = 'mobile-submenu-open'
const REGION_LS_KEY = 'wyld_us_state'

class MobileMenu extends HTMLElement {
  #abort
  #bodyObserver
  #lastOpen

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#initPanelNav(signal)
    this.#initRegion(signal)
    this.#observeBody()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#bodyObserver?.disconnect()
    this.#unlockScroll()
  }

  #initPanelNav(signal) {
    this.querySelectorAll('[data-open-panel]').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => this.#openPanel(btn.dataset.openPanel),
        { signal },
      )
    })

    // The back control lives in the header section, so listen at the document.
    document.addEventListener(
      'click',
      (e) => {
        const back = e.target.closest('[data-mobile-menu-back]')
        if (back) this.#openPanel('root')
      },
      { signal },
    )
  }

  #openPanel(key = 'root') {
    const target = key
    this.dataset.active = target

    this.querySelectorAll('[data-open-panel]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(btn.dataset.openPanel === target))
    })

    this.querySelectorAll('.view').forEach((view) => {
      view.classList.toggle('is-active', view.dataset.panel === target)
    })

    if (target === 'root') {
      document.body.classList.remove(BODY_SUB_CLASS)
    } else {
      document.body.classList.add(BODY_SUB_CLASS)
    }

    const view = this.querySelector(`.view[data-panel="${target}"]`)
    if (view) view.scrollTop = 0
  }

  #initRegion(signal) {
    const selects = this.querySelectorAll('[data-mobile-region-select]')
    if (!selects.length) return

    const stored = this.#safeGet(REGION_LS_KEY)
    if (stored) this.#applyState(selects, stored)

    selects.forEach((select) => {
      this.#sizeRegionSelect(select)
      select.addEventListener(
        'change',
        () => {
          const value = select.value
          this.#applyState(selects, value)
          try {
            localStorage.setItem(REGION_LS_KEY, value)
          } catch {
            /* private mode */
          }
          document.dispatchEvent(
            new CustomEvent('theme:region:state-changed', {
              bubbles: true,
              detail: { state: value },
            }),
          )
        },
        { signal },
      )
    })

    document.addEventListener(
      'theme:region:state-changed',
      (e) => {
        const value = e.detail?.state
        if (value) this.#applyState(selects, value)
      },
      { signal },
    )
  }

  #applyState(selects, value) {
    selects.forEach((select) => {
      if (Array.from(select.options).some((o) => o.value === value)) {
        select.value = value
      }
      this.#sizeRegionSelect(select)
    })
  }

  // The region font (Spline Sans Mono) is monospaced, so the select can track
  // the selected option's text via `ch`. With `box-sizing: border-box`, the
  // width is the text width plus the horizontal padding (10px + 36px) and
  // borders (2px x 2) defined in `mobile-menu.scss` — keep this in sync if those change.
  #sizeRegionSelect(select) {
    const option = select.options[select.selectedIndex]
    const length = option ? option.text.trim().length : 0
    select.style.width = `calc(${length}ch + 50px)`
  }

  #observeBody() {
    // Only react to an actual open/closed transition. `apply()` itself writes to
    // `document.body.classList` (via `#openPanel`), and `classList` mutations
    // re-serialize the `class` attribute even for no-op changes, which re-fires
    // this observer — without the transition guard that becomes an infinite loop.
    const apply = () => {
      const open = document.body.classList.contains(BODY_OPEN_CLASS)
      if (open === this.#lastOpen) return
      this.#lastOpen = open

      this.toggleAttribute('inert', !open)
      this.setAttribute('aria-hidden', open ? 'false' : 'true')

      if (open) {
        this.#lockScroll()
      } else {
        this.#unlockScroll()
        this.#openPanel('root')
      }
    }

    this.#bodyObserver = new MutationObserver(apply)
    this.#bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
    apply()
  }

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden'
  }

  #unlockScroll() {
    document.documentElement.style.overflow = ''
  }

  #safeGet(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu)
}
